import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import orderBy from 'lodash/orderBy';
import DateTimeExpression from 'utils/filter/expressions/DateTimeExpression';
import { getEnumValue } from '@instinet-analytics/core-lib';
import { CorporateActionType } from 'widgets/corpActions/data/CorporateActionType';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';
import { CorporateActionsCapsuleConfigState } from 'widgets/corpActions/capsule/store/CorporateActionsCapsuleConfigState';
import {
    CorpActionTypeHolidayEnum,
    isCorpActionHolidayType,
} from 'widgets/corpActions/data/HolidayType';
import { EnrichedSymbolEvent } from 'widgets/corpActions/store/data/earningsAftMktOnTMinus1.ts';

export interface Row {
    uuid?: string;
    type: 'Today' | 'Upcoming' | 'Historical';
    text: string;
    subText: string;
    isTminus1AftMftEarnings: boolean;
}

const todayComparisonExpression = new DateTimeExpression('Today');

const sort = (corporateActions: SymbolEvent[]) =>
    orderBy(corporateActions, ['effectiveDate'], ['desc']);

export function getCorporateActionCapsuleRowData(
    data: SymbolEvent[],
    config: CorporateActionsCapsuleConfigState
): Row[] {
    const { excludeHistorical, excludeToday, excludeUpcoming } = config;

    const upcoming = excludeUpcoming
        ? []
        : sort(
              data.filter((r) =>
                  todayComparisonExpression.testAfterThanDay(
                      r.effectiveDate ?? 0
                  )
              )
          );
    const today = excludeToday
        ? []
        : sort(
              data.filter((r) =>
                  todayComparisonExpression.testSameDay(r.effectiveDate ?? 0)
              )
          );
    const historical = excludeHistorical
        ? []
        : sort(
              data.filter((r) =>
                  todayComparisonExpression.testBeforeThanDay(
                      r.effectiveDate ?? 0
                  )
              )
          );

    const rows: Row[] = [
        ...upcoming
            .slice(upcoming.length - 3)
            .map((c) => toCorpActionRow('Upcoming', c)),
        ...(today.length > 0
            ? today.map((c) => toCorpActionRow('Today', c))
            : []),
        ...historical.slice(0, 2).map((c) => toCorpActionRow('Historical', c)),
    ];

    return orderBy(rows, ['effectiveDate'], ['desc']);
}

function toCorpActionRow(
    type: 'Today' | 'Upcoming' | 'Historical',
    corpAction: EnrichedSymbolEvent
) {
    let subText = '';
    if (corpAction.effectiveDate) {
        subText = getDaysDiffDesc(
            corpAction.effectiveDate,
            new Date().getTime()
        );
    }
    return {
        type,
        text: isCorpActionHolidayType(corpAction.type)
            ? getEnumValue(CorpActionTypeHolidayEnum, corpAction.type)
            : getEnumValue(CorporateActionType, corpAction.type),
        subText,
        uuid: corpAction.uuid,
        isTminus1AftMftEarnings: !!corpAction.isTminus1AftMftEarnings,
    };
}

export function getDaysDiffDesc(to: number, from: number): string {
    if (to === 0) {
        return '';
    }
    const fromDateTime = DateTime.fromMillis(to).startOf('day');
    const toDateTime = DateTime.fromMillis(from).startOf('day');

    const days = Math.abs(fromDateTime.diff(toDateTime, 'days').days);
    const months = Math.abs(fromDateTime.diff(toDateTime, 'months').months);

    if (days === 0) {
        return 'TODAY';
    }

    const duration =
        months >= 1
            ? `${Math.floor(months)} ${pluralize('month', Math.floor(months))}`
            : `${days} ${pluralize('day', days)}`;

    if (fromDateTime < toDateTime) {
        return `${duration} ago`;
    } else {
        return `in ${duration}`;
    }
}
